import {
  Anchor,
  Checkbox,
  Collapsible,
  IconChevronDown,
  IconChevronUp,
  Label,
  LabeledControl,
  Paragraph,
  TextButton,
} from '@zillow/constellation';
import { TERMS_OF_USE_URL, TERMS_RENTER_URL } from '../../constants/urls';
import type { RentalListing } from '../../graphql/queries';

type Props = {
  givesPermission: boolean;
  setGivesPermission: (givesPermission: boolean) => void;
  listingOwnerName: string;
  rentalListing: RentalListing;
  disabled?: boolean;
};

export function SubmissionCheckbox({
  givesPermission,
  setGivesPermission,
  listingOwnerName,
  rentalListing,
  disabled,
}: Props) {
  const street = rentalListing.address?.street;
  return (
    <>
      <LabeledControl
        disabled={disabled}
        label={
          <Label>
            I give{' '}
            <strong>
              {listingOwnerName ? `${listingOwnerName},` : 'the'} manager of {street}
            </strong>
            , permission to view my application information, credit report and background check, and to contact the
            employers and landlords I’ve listed herein.
          </Label>
        }
        control={
          <Checkbox
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGivesPermission(e.currentTarget.checked)}
            name="submissionPermission"
            checked={givesPermission}
          />
        }
      />
      <LocalDisclaimer rentalListing={rentalListing} />
      <Paragraph fontType="legal">
        By submitting your application, you agree to comply with the{' '}
        <Anchor href={TERMS_OF_USE_URL} target="_blank" rel="noopener noreferrer">
          Terms of Use
        </Anchor>{' '}
        and the{' '}
        <Anchor href={TERMS_RENTER_URL} target="_blank" rel="noopener noreferrer">
          Rental User Terms
        </Anchor>
        .
      </Paragraph>
    </>
  );
}

function LocalDisclaimer({ rentalListing }: { rentalListing: RentalListing }) {
  const city = rentalListing.address?.city;
  const state = rentalListing.address?.state;
  if (!city || !state) {
    return null;
  }
  let textContent = '';
  const cityState = `${city.toLowerCase()}, ${state.toLowerCase()}`;
  switch (cityState) {
    case 'seattle, wa':
      textContent = `As required by the ${city} Municipal Code, the landlord is prohibited from requiring disclosure, asking about, rejecting an applicant, or taking an adverse action based on any arrest record, conviction record, or criminal history, except for registry information as described in subsections 14.09.025.A.3, 14.09.025.A.4, and 14.09.025.A.5, and subject to the exclusions and legal requirements in Section 14.09.115. The landlord may consider permitted registry information. If the landlord does consider registry information, ${city} law gives you the right to provide supplemental information to the landlord, including after an adverse decision by the landlord.`;
      break;
    case 'berkeley, ca':
    case 'oakland, ca':
      textContent = `As required by the ${city} Municipal Code, the Landlord is prohibited from inquiring about, requiring the release or disclosure of, or if the release or disclosure is permitted, rejecting an Applicant, or taking an adverse action against the Applicant based on any arrest record, conviction record, or criminal history, subject to certain exceptions. In addition, the Landlord may not generally consider Sex Offender Registry information, but may do so based on certain exceptions. To the extent any adverse decision is based on the consideration of Criminal Background, ${city} law requires the Landlord to provide written notice to the Applicant.`;
      break;
    case 'ann arbor, mi':
      textContent = `Notice to Prospective Renters in Ann Arbor: The City of Ann Arbor prohibits housing providers from inquiring about, requiring the release or disclosure of, or if the release or disclosure is permitted, rejecting an Applicant, or taking an adverse action against the Applicant based on the Applicant’s Criminal History, subject to certain exceptions. To the extent any adverse decision is based on the consideration of Criminal Background, Ann Arbor law requires housing providers to provide written notice to the Applicant.`;
      break;
  }

  return (
    textContent && (
      <Collapsible
        // eslint-disable-next-line react/no-children-prop
        children={(collapsed: boolean, toggle: () => void) => (
          <>
            <TextButton
              onClick={toggle}
              icon={collapsed ? <IconChevronDown /> : <IconChevronUp />}
              iconSize="xs"
              marginBottom="xs"
              type="button"
            >
              Notice to {city} applicants
            </TextButton>
            {!collapsed && (
              <Paragraph fontType="finePrint" marginBottom="sm">
                {textContent}
              </Paragraph>
            )}
          </>
        )}
      />
    )
  );
}
